import { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ChevronLeft, ChevronRight, Menu, X } from "lucide-react";
import AuthManager from "../../managers/AuthManager";
import FeatureFlagManager from "../../managers/FeatureFlagManager";
import { PlayerTeamSearch } from "./player-team-search";
import { MenuItem } from "./components/menu-item";
import { UserSection, AuthButtons } from "./components/user-section";
import { NAV_ITEMS } from "./utils/nav-items";

export const Sidebar = () => {
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [authState, setAuthState] = useState({ isAuthenticated: false, isAnonymous: false });
  const [hasRecruitingAccess, setHasRecruitingAccess] = useState(false);

  const navigate = useNavigate();
  const location = useLocation();

  useEffect(() => {
    const unsubscribe = AuthManager.onAuthStateChanged(async (currentUser) => {
      setUser(currentUser);
      setAuthState({
        isAuthenticated: !!currentUser,
        isAnonymous: currentUser?.isAnonymous || false,
      });
      setHasRecruitingAccess(
        currentUser ? await FeatureFlagManager.hasAccessToFeature("recruiting") : false
      );
    });
    return unsubscribe;
  }, []);

  const handleSignOut = async () => {
    const result = await AuthManager.signOut();
    if (result.success) navigate("/signin");
  };

  const isActiveRoute = (path) => {
    if (path === "/") return location.pathname === "/";
    return (
      location.pathname === path ||
      location.pathname.startsWith(`${path}/`) ||
      location.pathname.startsWith(`${path}?`)
    );
  };

  const visibleNav = NAV_ITEMS.filter(
    (item) => !item.featureFlag || (item.featureFlag === "recruiting" && hasRecruitingAccess)
  );

  const isExpanded = !collapsed || mobileOpen;

  return (
    <>
      <button
        onClick={() => setMobileOpen(!mobileOpen)}
        className="lg:hidden fixed top-4 right-4 z-50 p-3 rounded-xl bg-white shadow-lg border border-gray-200 hover:shadow-xl transition-all duration-200"
      >
        {mobileOpen ? (
          <X className="w-6 h-6 text-gray-600" />
        ) : (
          <Menu className="w-6 h-6 text-gray-600" />
        )}
      </button>

      {mobileOpen && (
        <div
          className="fixed inset-0 bg-black bg-opacity-50 z-30 lg:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      <div
        className={`${mobileOpen ? "translate-x-0" : "-translate-x-full"} ${
          isExpanded ? "w-64" : "w-20"
        } lg:translate-x-0 fixed inset-y-0 left-0 z-40 transition-all duration-300 border-r border-gray-200 bg-white flex flex-col lg:relative`}
      >
        <div className="h-16 flex items-center justify-center border-b border-gray-200">
          {isExpanded ? (
            <div className="px-4 flex items-center space-x-3">
              <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg flex items-center justify-center shadow-sm">
                <span className="text-white font-bold text-xl">D3</span>
              </div>
              <span className="font-semibold text-gray-800 text-lg">Dashboard</span>
            </div>
          ) : (
            <div className="w-10 h-10 bg-gradient-to-r from-blue-600 to-blue-500 rounded-lg flex items-center justify-center shadow-sm">
              <span className="text-white font-bold text-xl">D3</span>
            </div>
          )}
        </div>

        <div className="flex-1 px-3 py-4 space-y-1 overflow-y-auto">
          <div className="mb-4">
            <PlayerTeamSearch collapsed={!isExpanded} />
          </div>
          {visibleNav.map((item) => (
            <MenuItem
              key={item.path}
              icon={item.icon}
              to={item.path}
              isActive={isActiveRoute(item.path)}
              collapsed={collapsed}
              mobileOpen={mobileOpen}
            >
              {item.label}
            </MenuItem>
          ))}
        </div>

        <div className="border-t border-gray-200 p-3 space-y-2">
          {authState.isAuthenticated && !authState.isAnonymous ? (
            <UserSection
              user={user}
              onSignOut={handleSignOut}
              collapsed={collapsed}
              mobileOpen={mobileOpen}
            />
          ) : (
            <AuthButtons collapsed={collapsed} mobileOpen={mobileOpen} />
          )}

          <button
            onClick={() => setCollapsed(!collapsed)}
            className={`hidden lg:flex w-full items-center h-10 px-3 text-gray-500 hover:bg-gray-100 hover:text-gray-700 rounded-lg transition-all duration-200 ${
              !isExpanded ? "justify-center" : ""
            }`}
          >
            {isExpanded ? (
              <>
                <ChevronLeft className="w-4 h-4" />
                <span className="ml-2 text-sm font-medium">Collapse</span>
              </>
            ) : (
              <ChevronRight className="w-4 h-4" />
            )}
          </button>
        </div>
      </div>
    </>
  );
};
